import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Bell, BellRing, CheckCircle2, ClipboardList, ExternalLink, Loader2, Mail, RefreshCw, Settings,
} from 'lucide-react';

/**
 * « À faire » (Carlos) — synthèse de tout ce qui attend une action :
 * ventes hors stock, prix CADBOT, devis chat, acomptes à commander, commandes sans suivi,
 * SumUp à confirmer, demandes de facture. Rafraîchi toutes les 60 s.
 */

const AUTO_REFRESH_MS = 60000;

// Mapping clé backend (FR, non traduite) -> clé i18n pour l'affichage ; repli sur la clé brute.
const CATEGORY_LABEL_KEYS = {
  ventes_hors_stock: 'adminTodo.cat.ventesHorsStock',
  cadbot_prix: 'adminTodo.cat.cadbotPrix',
  devis_chat: 'adminTodo.cat.devisChat',
  acomptes_a_commander: 'adminTodo.cat.acomptesACommander',
  commandes_sans_suivi: 'adminTodo.cat.commandesSansSuivi',
  sumup_a_confirmer: 'adminTodo.cat.sumupAConfirmer',
  facture_requests: 'adminTodo.cat.factureRequests',
};

const CATEGORY_KEYS = Object.keys(CATEGORY_LABEL_KEYS);

const asArray = (v) => (Array.isArray(v) ? v : []);
const asCount = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const normalizeGroups = (data) => asArray(data?.groups).map((g, i) => ({
  key: g?.key || `group-${i}`,
  rawKey: g?.key || '',
  label: g?.label || '',
  count: asCount(g?.count ?? asArray(g?.items).length),
  url: g?.url || '',
  items: asArray(g?.items).map((it, j) => ({
    id: it?.id ?? `${g?.key || i}-${j}`,
    title: it?.title || '—',
    subtitle: it?.subtitle || '',
    when: it?.when || '',
    url: it?.url || '',
  })),
}));

const normalizeSettings = (data) => {
  const cats = data?.categories && typeof data.categories === 'object' ? data.categories : {};
  const categories = {};
  CATEGORY_KEYS.forEach((k) => { categories[k] = cats[k] !== undefined ? !!cats[k] : true; });
  return {
    push: data?.push !== undefined ? !!data.push : false,
    digest: data?.digest !== undefined ? !!data.digest : false,
    categories,
  };
};

const countPillClass = (count) => (
  count > 0
    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    : 'bg-green-500/15 text-green-400 border-green-500/30'
);

const Toggle = ({ checked, onChange, disabled, label, hint, testId }) => (
  <label className={`flex items-start justify-between gap-3 py-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
    <span className="min-w-0">
      <span className="block text-sm text-gray-200">{label}</span>
      {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
    </span>
    <span className="relative inline-flex shrink-0 mt-0.5">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span className="w-10 h-6 rounded-full bg-white/10 border border-white/10 peer-checked:bg-green-600 peer-checked:border-green-500 transition-colors" />
      <span className="absolute top-1 left-1 w-4 h-4 rounded-full bg-gray-300 peer-checked:bg-white peer-checked:translate-x-4 transition-transform" />
    </span>
  </label>
);

export default function AdminTodoPage() {
  const { api } = useAuth();
  const { t, formatDate } = useLanguage();
  const navigate = useNavigate();

  const formatWhen = (when) => {
    if (!when) return '';
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) return String(when);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin >= 0 && diffMin < 1) return t('adminTodo.time.now');
    if (diffMin >= 0 && diffMin < 60) return t('adminTodo.time.minutesAgo', { n: diffMin });
    if (diffMin >= 0 && diffMin < 1440) return t('adminTodo.time.hoursAgo', { n: Math.floor(diffMin / 60) });
    return formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Libellé de groupe : label backend, sinon traduction de la catégorie, sinon clé brute, sinon repli.
  const groupLabel = (g) => g.label
    || (CATEGORY_LABEL_KEYS[g.rawKey] ? t(CATEGORY_LABEL_KEYS[g.rawKey]) : '')
    || g.rawKey
    || t('adminTodo.groupFallback');
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get('/admin/carlos/todo');
      if (!mountedRef.current) return;
      const g = normalizeGroups(res.data);
      setGroups(g);
      setTotal(asCount(res.data?.total ?? g.reduce((s, x) => s + x.count, 0)));
      setLastUpdate(new Date());
      setError('');
    } catch (e) {
      if (!mountedRef.current) return;
      const msg = e.response?.status === 404
        ? t('adminTodo.error.endpointUnavailable')
        : (e.response?.data?.detail || e.message || t('adminTodo.error.load'));
      setError(typeof msg === 'string' ? msg : t('adminTodo.error.load'));
      if (!silent) toast.error(typeof msg === 'string' ? msg : t('adminTodo.error.load'));
    } finally {
      if (mountedRef.current) { setLoading(false); setRefreshing(false); }
    }
  }, [api, t]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/admin/carlos/todo/settings');
      if (!mountedRef.current) return;
      setSettings(normalizeSettings(res.data));
      setSettingsError('');
    } catch (e) {
      if (!mountedRef.current) return;
      setSettings(normalizeSettings(null));
      setSettingsError(e.response?.status === 404 ? t('adminTodo.settings.unavailable') : (e.response?.data?.detail || t('adminTodo.settings.notLoaded')));
    }
  }, [api, t]);

  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

  // Auto-refresh toutes les 60 s (silencieux), en pause quand l'onglet est caché.
  useEffect(() => {
    const tick = () => { if (document.visibilityState !== 'hidden') load({ silent: true }); };
    const id = setInterval(tick, AUTO_REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load({ silent: true }); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);

  const saveSettings = async (patch) => {
    if (!settings) return;
    const next = {
      ...settings,
      ...patch,
      categories: { ...settings.categories, ...(patch.categories || {}) },
    };
    const prev = settings;
    setSettings(next);
    setSavingSettings(true);
    try {
      const res = await api.put('/admin/carlos/todo/settings', next);
      if (!mountedRef.current) return;
      setSettings(res.data && typeof res.data === 'object' && 'categories' in res.data ? normalizeSettings(res.data) : next);
      toast.success(t('adminTodo.settings.saved'));
    } catch (e) {
      if (!mountedRef.current) return;
      setSettings(prev);
      toast.error(e.response?.data?.detail || t('adminTodo.settings.saveError'));
    } finally {
      if (mountedRef.current) setSavingSettings(false);
    }
  };

  const openUrl = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) { window.open(url, '_blank', 'noopener'); return; }
    navigate(url);
  };

  const pendingGroups = groups.filter((g) => g.count > 0);
  const emptyGroups = groups.filter((g) => g.count === 0);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 fade-in" data-testid="admin-todo">
        <PageHeader
          title={(
            <span className="inline-flex items-center gap-2">
              {t('adminTodo.title')}
              <span
                className={`text-xs px-2 py-0.5 rounded-full border font-bold tabular-nums ${total > 0 ? 'bg-red-600 text-white border-red-500' : 'bg-green-500/15 text-green-400 border-green-500/30'}`}
                data-testid="todo-total-badge"
              >
                {total}
              </span>
            </span>
          )}
          subtitle={lastUpdate ? t('adminTodo.subtitleUpdated', { time: formatDate(lastUpdate, { hour: '2-digit', minute: '2-digit' }) }) : t('adminTodo.subtitleDefault')}
          icon={ClipboardList}
          actions={(
            <>
              <Button variant="outline" size="sm" onClick={() => setShowSettings((s) => !s)} className="border-gray-600 text-gray-300" data-testid="todo-settings-btn">
                <Settings className="w-4 h-4 mr-2" /> {t('adminTodo.notifications')}
              </Button>
              <Button size="sm" onClick={() => load({ silent: true })} disabled={refreshing || loading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="todo-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> {t('adminTodo.refresh')}
              </Button>
            </>
          )}
        />

        {showSettings && (
          <div className="glass p-4 sm:p-5 rounded-2xl border border-white/10 space-y-2" data-testid="todo-settings">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-white font-bold flex items-center gap-2"><Bell className="w-5 h-5 text-red-400" /> {t('adminTodo.notifications')}</h2>
              {savingSettings && <span className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('adminTodo.settings.saving')}</span>}
            </div>
            {settingsError && <p className="text-xs text-amber-400">{settingsError}</p>}
            {!settings ? (
              <p className="text-sm text-gray-500">{t('adminTodo.settings.loading')}</p>
            ) : (
              <>
                <div className="divide-y divide-white/5">
                  <Toggle
                    label={t('adminTodo.settings.pushLabel')}
                    hint={t('adminTodo.settings.pushHint')}
                    checked={settings.push}
                    disabled={savingSettings}
                    onChange={(v) => saveSettings({ push: v })}
                    testId="todo-setting-push"
                  />
                  <Toggle
                    label={t('adminTodo.settings.digestLabel')}
                    hint={t('adminTodo.settings.digestHint')}
                    checked={settings.digest}
                    disabled={savingSettings}
                    onChange={(v) => saveSettings({ digest: v })}
                    testId="todo-setting-digest"
                  />
                </div>
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('adminTodo.settings.categoriesTracked')}</p>
                  <div className="divide-y divide-white/5">
                    {CATEGORY_KEYS.map((k) => (
                      <Toggle
                        key={k}
                        label={t(CATEGORY_LABEL_KEYS[k])}
                        checked={settings.categories[k]}
                        disabled={savingSettings}
                        onChange={(v) => saveSettings({ categories: { [k]: v } })}
                        testId={`todo-setting-cat-${k}`}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-red-300 bg-red-950/40 border border-red-500/30 rounded-xl px-3 py-2" data-testid="todo-error">{error}</p>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : groups.length === 0 ? (
          <div className="glass p-10 rounded-2xl border border-white/10 text-center text-gray-400">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500" />
            {t('adminTodo.allClear')}
          </div>
        ) : (
          <>
            {pendingGroups.length === 0 && (
              <div className="glass p-6 rounded-2xl border border-green-500/20 text-center text-green-400 flex items-center justify-center gap-2" data-testid="todo-all-clear">
                <CheckCircle2 className="w-5 h-5" /> {t('adminTodo.allClear')}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...pendingGroups, ...emptyGroups].map((g) => (
                <div
                  key={g.key}
                  className={`glass rounded-2xl border p-4 sm:p-5 space-y-3 ${g.count > 0 ? 'border-amber-500/20' : 'border-white/10'}`}
                  data-testid={`todo-group-${g.key}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-white font-bold text-base leading-tight min-w-0">{groupLabel(g)}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-bold tabular-nums shrink-0 ${countPillClass(g.count)}`}>
                      {g.count}
                    </span>
                  </div>

                  {g.items.length === 0 ? (
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" /> {t('adminTodo.groupNothingPending')}
                    </p>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {g.items.map((it) => (
                        <li key={it.id} className="py-2.5 flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate">{it.title}</p>
                            {it.subtitle && <p className="text-xs text-gray-400 truncate">{it.subtitle}</p>}
                            {it.when && <p className="text-[11px] text-gray-500 mt-0.5">{formatWhen(it.when)}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => openUrl(it.url || g.url)}
                            disabled={!it.url && !g.url}
                            className="min-h-[36px] px-3 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-gray-100 text-xs font-semibold flex items-center gap-1 shrink-0"
                            data-testid={`todo-open-${g.key}-${it.id}`}
                          >
                            {t('adminTodo.open')} <ExternalLink className="w-3 h-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {g.count > g.items.length && g.url && (
                    <button
                      type="button"
                      onClick={() => openUrl(g.url)}
                      className="text-xs text-red-400 hover:underline"
                      data-testid={`todo-group-more-${g.key}`}
                    >
                      {t('adminTodo.seeAll', { count: g.count })}
                    </button>
                  )}
                  {g.count > 0 && g.items.length === g.count && g.url && (
                    <button
                      type="button"
                      onClick={() => openUrl(g.url)}
                      className="text-xs text-gray-400 hover:text-white hover:underline"
                    >
                      {t('adminTodo.openPage')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <p className="text-xs text-gray-600 flex items-center gap-2">
          {settings?.push ? <BellRing className="w-3.5 h-3.5 text-green-500" /> : <Bell className="w-3.5 h-3.5" />}
          {settings?.push ? t('adminTodo.pushOn') : t('adminTodo.pushOff')}
          {' · '}
          <Mail className="w-3.5 h-3.5" />
          {settings?.digest ? t('adminTodo.digestOn') : t('adminTodo.digestOff')}
        </p>
      </div>
    </Layout>
  );
}
